
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Checkbox } from '../../../components/ui/checkbox';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';
import { CreditNoteMode, Invoice, legacyApi } from '../../../services/legacyApi';

interface CreditNoteModalProps {
    invoice: Invoice;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreditNoteModal: React.FC<CreditNoteModalProps> = ({ invoice, isOpen, onClose, onSuccess }) => {
    const [mode, setMode] = useState<CreditNoteMode>('RETURN_ITEMS');
    const [selectedItems, setSelectedItems] = useState<Record<number, number>>({});
    const [invoiceDetail, setInvoiceDetail] = useState<Invoice | null>(null);
    const [chargebackAmount, setChargebackAmount] = useState<string>('');
    const [chargebackReason, setChargebackReason] = useState<string>('');
    const [chargebackTax, setChargebackTax] = useState<string>('5');
    const [chargebackRemarks, setChargebackRemarks] = useState<string>('');
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isOpen || !invoice?.invoice_id) return;
        setSelectedItems({});
        setChargebackTax(String(Number(invoice?.vat_percent ?? 5) || 5));
        let cancelled = false;
        setLoadingDetails(true);
        legacyApi.sales.getInvoice(invoice.invoice_id)
            .then((full) => {
                if (!cancelled) setInvoiceDetail(full);
            })
            .catch((error: any) => {
                if (!cancelled) {
                    toast.error(error?.message || "Failed to load invoice details");
                    setInvoiceDetail(invoice);
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingDetails(false);
            });
        return () => { cancelled = true; };
    }, [isOpen, invoice]);

    useEffect(() => {
        if (!isOpen) {
            setMode('RETURN_ITEMS');
            setInvoiceDetail(null);
            setSelectedItems({});
            setChargebackAmount('');
            setChargebackReason('');
            setChargebackTax('5');
            setChargebackRemarks('');
        }
    }, [isOpen]);

    const items = (invoiceDetail?.details || invoice.details || []).filter((item: any) => Number(item?.quantity || 0) > 0);

    const handleToggle = (lineId: number, maxQty: number) => {
        setSelectedItems(prev => {
            const next = { ...prev };
            if (next[lineId] !== undefined) {
                delete next[lineId];
            } else {
                next[lineId] = maxQty; // Default to full return
            }
            return next;
        });
    };

    const handleQtyChange = (lineId: number, qty: number, maxQty: number) => {
        if (qty < 0) qty = 0;
        if (qty > maxQty) qty = maxQty;
        setSelectedItems(prev => ({ ...prev, [lineId]: qty }));
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            let draft;
            if (mode === 'RETURN_ITEMS') {
                const returnItems = Object.entries(selectedItems).map(([id, qty]) => ({
                    invoice_detail_id: parseInt(id, 10),
                    quantity: qty,
                }));
                if (returnItems.length === 0) {
                    toast.error("Please select items to return");
                    return;
                }
                draft = await legacyApi.sales.createCreditNote({
                    source_invoice_id: invoice.invoice_id,
                    mode,
                    items: returnItems,
                    reason: 'Customer return',
                    remarks: `Created from invoice HC/INV/${String(invoice.invoice_id).padStart(4, '0')}`,
                });
            } else {
                const amount = parseFloat(chargebackAmount);
                const tax = parseFloat(chargebackTax);
                const reason = chargebackReason.trim();
                if (!Number.isFinite(amount) || amount <= 0) {
                    toast.error('Chargeback amount must be greater than zero');
                    return;
                }
                if (!reason) {
                    toast.error('Reason is required for chargeback adjustment');
                    return;
                }
                if (!Number.isFinite(tax) || tax < 0) {
                    toast.error('Tax percent must be zero or greater');
                    return;
                }
                draft = await legacyApi.sales.createCreditNote({
                    source_invoice_id: invoice.invoice_id,
                    mode,
                    adjustment_amount: amount,
                    vat_percent: tax,
                    reason,
                    remarks: chargebackRemarks.trim() || `Chargeback from invoice HC/INV/${String(invoice.invoice_id).padStart(4, '0')}`,
                });
            }
            await legacyApi.sales.transitionCreditNote(draft.cn_id, 'post');
            toast.success(`Credit Note ${draft.cn_number} posted`);
            onSuccess();
            onClose();
        } catch (error: any) {
            toast.error(error.message || "Failed to create Credit Note");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle>Create Credit Note - {invoice.customer_name}</DialogTitle>
                </DialogHeader>

                <div className="py-4">
                    <div className="flex gap-2 mb-4">
                        <Button
                            type="button"
                            variant={mode === 'RETURN_ITEMS' ? 'default' : 'outline'}
                            onClick={() => setMode('RETURN_ITEMS')}
                            disabled={loading}
                        >
                            Return Items
                        </Button>
                        <Button
                            type="button"
                            variant={mode === 'CHARGEBACK_ADJUSTMENT' ? 'default' : 'outline'}
                            onClick={() => setMode('CHARGEBACK_ADJUSTMENT')}
                            disabled={loading}
                        >
                            Chargeback Adjustment
                        </Button>
                    </div>

                    {mode === 'RETURN_ITEMS' ? (
                        <>
                            <p className="text-sm text-muted-foreground mb-4">Select items and quantities to return for Invoice #{invoice.invoice_id}</p>
                            <div className="border rounded-md overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted/50">
                                        <tr>
                                            <th className="p-2 w-10"></th>
                                            <th className="p-2 text-left">Item</th>
                                            <th className="p-2 text-right">Invoiced Qty</th>
                                            <th className="p-2 text-right">Return Qty</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loadingDetails && (
                                            <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">Loading invoice items...</td></tr>
                                        )}
                                        {items.map((item: any) => (
                                            <tr key={item.invoice_detail_id} className="border-t">
                                                <td className="p-2 text-center">
                                                    <Checkbox
                                                        checked={selectedItems[item.invoice_detail_id] !== undefined}
                                                        onCheckedChange={() => handleToggle(item.invoice_detail_id, Number(item.quantity || 0))}
                                                    />
                                                </td>
                                                <td className="p-2">{item.part_name}</td>
                                                <td className="p-2 text-right">{item.quantity}</td>
                                                <td className="p-2 text-right">
                                                    <Input
                                                        type="number"
                                                        value={selectedItems[item.invoice_detail_id] || ''}
                                                        onChange={(e) => handleQtyChange(item.invoice_detail_id, parseFloat(e.target.value), Number(item.quantity || 0))}
                                                        disabled={selectedItems[item.invoice_detail_id] === undefined}
                                                        className="w-24 ml-auto h-8"
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                        {!loadingDetails && items.length === 0 && (
                                            <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">No invoice items available.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    ) : (
                        <div className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                Create a value-only credit note adjustment without item quantity returns.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="chargeback-amount">Adjustment Amount</Label>
                                    <Input
                                        id="chargeback-amount"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={chargebackAmount}
                                        onChange={(e) => setChargebackAmount(e.target.value)}
                                        placeholder="0.00"
                                        disabled={loading}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="chargeback-tax">Tax %</Label>
                                    <Input
                                        id="chargeback-tax"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={chargebackTax}
                                        onChange={(e) => setChargebackTax(e.target.value)}
                                        placeholder="5"
                                        disabled={loading}
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="chargeback-reason">Reason</Label>
                                <Input
                                    id="chargeback-reason"
                                    value={chargebackReason}
                                    onChange={(e) => setChargebackReason(e.target.value)}
                                    placeholder="Customer chargeback reason"
                                    disabled={loading}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="chargeback-remarks">Remarks (Optional)</Label>
                                <Textarea
                                    id="chargeback-remarks"
                                    value={chargebackRemarks}
                                    onChange={(e) => setChargebackRemarks(e.target.value)}
                                    placeholder="Additional notes"
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={
                            loading ||
                            (mode === 'RETURN_ITEMS'
                                ? (loadingDetails || Object.keys(selectedItems).length === 0)
                                : false)
                        }
                    >
                        {loading ? "Processing..." : "Create Credit Note"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
